import { useState, useEffect } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { UserProfile } from '../types/user';
import { getUserDocument, updateEmailVerificationStatus } from '../lib/userService';
import { useAuth } from './useAuth';

export const useUserProfile = () => {
  const { user } = useAuth();
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user?.uid) {
      setUserProfile(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    // Set up real-time listener for user profile changes
    const unsubscribe = onSnapshot(
      doc(db, 'users', user.uid),
      async (docSnapshot) => {
        try {
          if (docSnapshot.exists()) {
            const profile = docSnapshot.data() as UserProfile;
            setUserProfile(profile);

            // Update email verification status if it has changed
            if (user.emailVerified !== undefined && profile.emailVerified !== user.emailVerified) {
              await updateEmailVerificationStatus(user.uid, user.emailVerified);
              setUserProfile(prev => prev ? { ...prev, emailVerified: user.emailVerified } : null);
            }
          } else {
            setError('User profile not found');
          }
        } catch (err) {
          console.error('Error processing user profile:', err);
          setError('Failed to load user profile');
        } finally {
          setLoading(false);
        }
      },
      (err) => {
        console.error('Error fetching user profile:', err);
        setError('Failed to load user profile');
        setLoading(false);
      }
    );

    // Cleanup listener on unmount or user change
    return () => unsubscribe();
  }, [user?.uid, user?.emailVerified]);

  const refreshProfile = async () => {
    if (user) {
      try {
        setError(null);
        const profile = await getUserDocument(user.uid);
        setUserProfile(profile);
      } catch (err) {
        console.error('Error refreshing user profile:', err);
        setError('Failed to refresh user profile');
      }
    }
  };

  return {
    userProfile,
    loading,
    error,
    refreshProfile,
  };
};